<template>
    <div>
        <div class="kp-toolbar">
            <p class="kp-intro">
                A plan sets the default limits for every key on it. Leave a limit on <strong>Unlimited</strong> to remove it.
                Changes apply to existing keys immediately.
            </p>
            <button class="ak-btn primary" @click="openCreate">+ New API Plan</button>
        </div>

        <div v-if="plans.length" class="kp-table-wrap">
            <table class="kp-table">
                <thead>
                    <tr>
                        <th>Plan</th>
                        <th>Tenders / day</th>
                        <th>Tenders / month</th>
                        <th>Requests / day</th>
                        <th>Req / min</th>
                        <th>Portals</th>
                        <th>Features</th>
                        <th>Keys</th>
                        <th>Status</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="p in plans" :key="p.id" :class="{ dim: !p.is_active }">
                        <td>
                            <div class="kp-name">{{ p.name }}</div>
                            <div v-if="p.description" class="kp-desc">{{ p.description }}</div>
                        </td>
                        <td>{{ fmt(p.max_tenders_per_day) }}</td>
                        <td>{{ fmt(p.max_tenders_per_month) }}</td>
                        <td>{{ fmt(p.max_requests_per_day) }}</td>
                        <td>{{ fmt(p.rate_limit_per_min) }}</td>
                        <td>
                            <span v-if="p.allowed_portals == null">All</span>
                            <span v-else>{{ p.allowed_portals.join(', ') || 'None' }}</span>
                        </td>
                        <td>
                            <span v-for="f in enabledFeatures(p)" :key="f" class="ak-badge info">{{ f }}</span>
                            <span v-if="!enabledFeatures(p).length" class="kp-muted">—</span>
                        </td>
                        <td>{{ p.key_count ?? 0 }}</td>
                        <td><span class="ak-badge" :class="p.is_active ? 'ok' : 'off'">{{ p.is_active ? 'Active' : 'Inactive' }}</span></td>
                        <td class="kp-actions">
                            <button class="ak-btn ghost sm" @click="openEdit(p)">Edit</button>
                            <button class="ak-btn ghost sm danger-text" @click="askDelete(p)">Delete</button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div v-else class="kp-empty">
            <p>No API plans yet.</p>
            <button class="ak-btn ghost" @click="openCreate">Create your first API plan</button>
        </div>

        <!-- Create / edit -->
        <div v-if="showModal" class="ak-overlay" @click.self="closeModal">
            <div class="ak-modal wide">
                <div class="ak-modal-head">
                    <div class="ak-modal-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                        </svg>
                    </div>
                    <div>
                        <h2>{{ isEditing ? 'Edit API Plan' : 'New API Plan' }}</h2>
                        <p>Limits, portals and features for keys on this plan</p>
                    </div>
                </div>
                <div class="ak-modal-body">
                    <form class="ak-form" @submit.prevent="save">
                        <div class="ak-row">
                            <div>
                                <label class="ak-label">Plan name</label>
                                <input class="ak-input" v-model="form.name" required placeholder="e.g. Starter">
                            </div>
                            <div>
                                <label class="ak-label">Description</label>
                                <input class="ak-input" v-model="form.description" placeholder="Optional">
                            </div>
                        </div>

                        <ApiLimitsEditor v-model="state" :portals="portals" :allow-inherit="false" />

                        <label class="ak-check">
                            <input type="checkbox" v-model="form.is_active">
                            Plan is active (can be picked for new keys)
                        </label>

                        <div v-if="formError" class="ak-error">{{ formError }}</div>

                        <div class="ak-actions">
                            <button type="button" class="ak-btn ghost" @click="closeModal">Cancel</button>
                            <button type="submit" class="ak-btn primary" :disabled="saving">
                                <span v-if="saving" class="ak-spinner"></span>
                                {{ saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Plan') }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- Delete confirm -->
        <div v-if="toDelete" class="ak-overlay" @click.self="closeDelete">
            <div class="ak-modal narrow ak-confirm">
                <h3>Delete API plan?</h3>
                <p v-if="!deleteConflict">
                    "{{ toDelete.name }}" will be removed. This cannot be undone.
                </p>
                <p v-else class="ak-error left">{{ deleteConflict }}</p>
                <div class="ak-actions">
                    <button class="ak-btn ghost" @click="closeDelete">Cancel</button>
                    <button v-if="deleteConflict && toDelete.is_active" class="ak-btn primary" :disabled="saving" @click="deactivate">
                        {{ saving ? 'Working...' : 'Deactivate instead' }}
                    </button>
                    <button v-if="!deleteConflict" class="ak-btn danger" :disabled="saving" @click="doDelete">
                        <span v-if="saving" class="ak-spinner"></span>
                        {{ saving ? 'Deleting...' : 'Yes, Delete' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import ApiLimitsEditor from './ApiLimitsEditor.vue';
import {
    FEATURE_FLAGS, authHeaders, errorMessage, makePlanState, planPayload, validateState, fmtLimit,
} from './apiKeysCommon';

export default {
    name: 'ApiKeyPlans',
    components: { ApiLimitsEditor },
    props: {
        plans: { type: Array, required: true },
        portals: { type: Array, default: () => [] },
    },
    emits: ['changed', 'notify'],
    data() {
        return {
            showModal: false,
            isEditing: false,
            saving: false,
            formError: '',
            form: { id: null, name: '', description: '', is_active: true },
            state: makePlanState(null),
            toDelete: null,
            deleteConflict: '',
        };
    },
    methods: {
        fmt(v) { return fmtLimit(v); },
        enabledFeatures(p) {
            return FEATURE_FLAGS.filter(f => p.features && p.features[f.key]).map(f => f.label);
        },
        openCreate() {
            this.isEditing = false;
            this.form = { id: null, name: '', description: '', is_active: true };
            this.state = makePlanState(null);
            this.formError = '';
            this.showModal = true;
        },
        openEdit(p) {
            this.isEditing = true;
            this.form = { id: p.id, name: p.name, description: p.description || '', is_active: p.is_active };
            this.state = makePlanState(p);
            this.formError = '';
            this.showModal = true;
        },
        closeModal() {
            if (!this.saving) this.showModal = false;
        },
        async save() {
            if (this.saving) return;
            this.formError = '';
            if (!this.form.name.trim()) { this.formError = 'Plan name is required'; return; }
            const problem = validateState(this.state);
            if (problem) { this.formError = problem; return; }

            const body = {
                name: this.form.name.trim(),
                description: this.form.description.trim() || null,
                is_active: this.form.is_active,
                ...planPayload(this.state),
            };
            this.saving = true;
            try {
                if (this.isEditing) {
                    await axios.patch(`${this.$API_URL}/super_admin/api-plans/${this.form.id}`, body, { headers: authHeaders() });
                    this.$emit('notify', { type: 'success', message: 'API plan updated' });
                } else {
                    await axios.post(`${this.$API_URL}/super_admin/api-plans`, body, { headers: authHeaders() });
                    this.$emit('notify', { type: 'success', message: 'API plan created' });
                }
                this.showModal = false;
                this.$emit('changed');
            } catch (err) {
                this.formError = errorMessage(err, 'Failed to save plan');
            } finally {
                this.saving = false;
            }
        },
        askDelete(p) {
            this.toDelete = p;
            this.deleteConflict = '';
        },
        closeDelete() {
            if (!this.saving) { this.toDelete = null; this.deleteConflict = ''; }
        },
        async doDelete() {
            if (this.saving || !this.toDelete) return;
            this.saving = true;
            try {
                const res = await axios.delete(`${this.$API_URL}/super_admin/api-plans/${this.toDelete.id}`, { headers: authHeaders() });
                this.$emit('notify', { type: 'success', message: res.data?.message || 'API plan deleted' });
                this.toDelete = null;
                this.$emit('changed');
            } catch (err) {
                if (err.response?.status === 409) {
                    this.deleteConflict = `${errorMessage(err, 'This plan is used by active keys.')} Deactivate the plan instead.`;
                } else {
                    this.$emit('notify', { type: 'error', message: errorMessage(err, 'Failed to delete plan') });
                    this.toDelete = null;
                }
            } finally {
                this.saving = false;
            }
        },
        async deactivate() {
            if (this.saving || !this.toDelete) return;
            this.saving = true;
            try {
                await axios.patch(`${this.$API_URL}/super_admin/api-plans/${this.toDelete.id}`, { is_active: false }, { headers: authHeaders() });
                this.$emit('notify', { type: 'success', message: 'API plan deactivated' });
                this.toDelete = null;
                this.deleteConflict = '';
                this.$emit('changed');
            } catch (err) {
                this.deleteConflict = errorMessage(err, 'Failed to deactivate plan');
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.kp-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.25rem; }
.kp-intro { margin: 0; max-width: 640px; font-size: 0.85rem; color: var(--text-secondary); }
.kp-table-wrap { background: #fff; border: 1px solid #eef2f7; border-radius: 16px; overflow-x: auto; }
.kp-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.kp-table th {
    text-align: left; padding: 0.8rem 1rem; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.06em;
    color: var(--text-label); background: #f8fafc; border-bottom: 1px solid #e2e8f0; white-space: nowrap;
}
.kp-table td { padding: 0.85rem 1rem; border-bottom: 1px solid #f1f5f9; color: var(--text-primary); vertical-align: top; }
.kp-table tr:last-child td { border-bottom: none; }
.kp-table tr.dim td { opacity: 0.6; }
.kp-name { font-weight: 700; }
.kp-desc { font-size: 0.75rem; color: #94a3b8; margin-top: 2px; max-width: 220px; }
.kp-muted { color: #cbd5e1; }
.kp-actions { white-space: nowrap; text-align: right; }
.kp-actions .ak-btn + .ak-btn { margin-left: 6px; }
.kp-empty { padding: 4rem 2rem; text-align: center; color: #94a3b8; background: #fff; border: 1px dashed #e2e8f0; border-radius: 20px; }
</style>
